/** Auth store (Pinia).
 *
 * Holds:
 *   - admin JWT (delegated to authService for storage)
 *   - principal (user + admin info)
 *   - loading flag for the login transition
 *
 * Exposes:
 *   - isAuthenticated: token exists
 *   - isAdmin: admin.role === 'system_admin'
 *   - login(), logout(), refresh()
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { authService, type StoredPrincipal } from '@/services/auth';
import { apiClient } from '@/services/api-client';
import type { AdminLoginRequest } from '@/types';
import { AdminApiError } from '@/services/api';

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(authService.loadToken());
  const principal = ref<StoredPrincipal | null>(authService.loadPrincipal());
  const loading = ref(false);
  const lastError = ref<AdminApiError | null>(null);

  const isAuthenticated = computed(() => token.value !== null);
  const isAdmin = computed(
    () => principal.value?.admin.role === 'system_admin' &&
          principal.value?.admin.status === 'active',
  );

  async function login(payload: AdminLoginRequest) {
    loading.value = true;
    lastError.value = null;
    try {
      const res = await authService.login(apiClient, payload);
      token.value = res.access_token;
      principal.value = { user: res.user, admin: res.admin };
      return res;
    } catch (err) {
      if (err instanceof AdminApiError) lastError.value = err;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    loading.value = true;
    try {
      await authService.logout(apiClient);
    } catch (err) {
      // Even on failure, clear the local token so the user is logged out.
      if (err instanceof AdminApiError) {
        lastError.value = err;
      }
    } finally {
      token.value = null;
      principal.value = null;
      loading.value = false;
    }
  }

  async function refresh() {
    if (!token.value) return null;
    try {
      const me = await authService.me(apiClient);
      principal.value = { user: me.user, admin: me.admin };
      return principal.value;
    } catch (err) {
      if (err instanceof AdminApiError && err.status === 401) {
        token.value = null;
        principal.value = null;
      }
      throw err;
    }
  }

  return {
    token,
    principal,
    loading,
    lastError,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    refresh,
  };
});